import { useState, type CSSProperties } from 'react'
import { FlaskConical, FileSearch, ChevronRight, Building2, Users, Sparkles, Loader2, AlertTriangle, Check, CheckSquare, Square } from 'lucide-react'
import { CopyButton, inputCls, labelCls, SectionLabel } from '../styles'
import { ENVIRONMENTS } from '../data'
import { makeAccountRecord } from '../utils'
import type { EnvAccounts, EnvId } from '../types'
import { VendorRegistrationPanel } from './VendorRegistrationPanel'
import { PlatformConfigPanel } from './PlatformConfigPanel'
import { EnvironmentSelector } from './EnvironmentSelector'
import { AccountSetupGuidedView } from './AccountSetupGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import { NewExistingToggle } from './NewExistingToggle'
import type { CustomerMode } from './NewExistingToggle'
import { SavedJourneysPanel } from './SavedJourneysPanel'
import { JourneyConfigSummary } from './JourneyConfigSummary'
import { platformJourneysApi, platformJourneyAiApi } from '@api/client'
import type { PlatformJourneyRecord } from '@api/client'
import { useOpsCenterStore } from '@features/dashboards/components/OpsCenterMockup/store'
import type { Workflow } from '@features/dashboards/components/OpsCenterMockup/types'
import type {
  AiChatMessage, AiConfigureSuggestions, AiObjectives, AiStageKey, AiStageSuggestionState, AiWorkflowSuggestion, AiIdSuggestion,
} from '../aiConfigure'
import { V1_RECIPES } from '@features/scope/types'
import { AiSuggestionBanner } from './AiSuggestionBanner'
import { AiConfigureChatPanel } from './AiConfigureChatPanel'
import { ObjectivesCard } from './ObjectivesCard'
import type { IdvAiSuggestion } from './IdvConfigPanel'
import type { KybAiSuggestion } from './KybConfigPanel'
import type { AmlScreeningConfig, DuplicateRuleConfig, TmRuleConfig } from '../types'

// Human-readable labels for each key AI Configure from Scope can return —
// covers every AiConfigureSuggestions key (see aiConfigure.ts), used only for
// the post-generate summary chips below, not the stages themselves.
const AI_STAGE_LABELS: Record<string, string> = {
  idv: 'IDV & Biometrics',
  cascade: 'KYC + Cascade',
  duplicates: 'Duplicate Detection',
  risk: 'Risk Profile',
  kyb: 'KYB',
  aml: 'AML',
  fraud: 'Fraud',
  tm: 'Transaction Monitoring',
  workflow: 'Workflow / Recipe',
}

const COUNTRIES = [
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
  { code: 'SG', label: 'Singapore' },
  { code: 'CA', label: 'Canada' },
]

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function envBadgeStyle(env: EnvId): CSSProperties {
  switch (env) {
    case 'prod': return { background: 'rgba(248,113,113,.12)', color: '#b91c1c' }
    case 'preprod': return { background: 'rgba(167,139,250,.12)', color: '#7c3aed' }
    case 'uat': return { background: 'rgba(251,191,36,.12)', color: '#b45309' }
    default: return { background: 'rgba(96,165,250,.12)', color: '#2563eb' }
  }
}

interface AccountSetupStageProps {
  customerMode: CustomerMode
  onCustomerModeChange: (mode: CustomerMode) => void
  existingCustomer: ExistingCustomerSelection | null
  onExistingCustomerChange: (selection: ExistingCustomerSelection | null) => void
  /** True once the tool shell has auto-resolved `existingCustomer` from a
   * `?dealId=` in the URL — i.e. this deal's OWN Scope record, not a
   * different customer the rep searched for to test with. Changes the New
   * Customer mode box below from a "pick anyone to test with" framing to
   * "this is the Scope record for this deal". */
  hasDealContext: boolean
  onRunningChange: (running: boolean) => void
  onComplete: (envAccounts: EnvAccounts[], customerName: string) => void
  /** Shared across every stage — picking Guided here keeps every later stage in
   * Guided too, instead of each one independently defaulting back to Classic. */
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  /** The saved Platform Journey record for this new-customer walkthrough, if
   * one has been created/loaded — null until Account Setup provisions accounts
   * or a saved customer is reloaded. */
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneyLoaded: (record: PlatformJourneyRecord) => void
  /** Reports a freshly-generated batch of AI suggestions up to the tool shell,
   * which fans them out to every stage that has one pending. */
  onAiSuggestions: (suggestions: AiConfigureSuggestions) => void
  /** Reports a chat-driven revision to a single stage's suggestion — a full
   * replacement of that stage's object, and re-opens it for review without
   * touching any other stage. See index.tsx's handleAiSuggestionPatch. */
  onAiSuggestionPatch: (stage: AiStageKey, suggestion: unknown) => void
  /** The current batch, read back from the tool shell — used here only to
   * resolve the recommended workflow recipe's rationale/confidence for the
   * confirm/reject widget below; every other stage reads its own slice the
   * same way. */
  aiSuggestions: AiConfigureSuggestions | null
  /** Lifted to the tool shell so every stage's own banner and this stage's
   * checklist below share one apply/dismiss state per suggestion, instead of
   * drifting out of sync with separate copies. */
  idvAi: AiStageSuggestionState<IdvAiSuggestion>
  cascadeAi: AiStageSuggestionState<AiIdSuggestion[]>
  duplicatesAi: AiStageSuggestionState<DuplicateRuleConfig[]>
  riskAi: AiStageSuggestionState<AiIdSuggestion[]>
  kybAi: AiStageSuggestionState<KybAiSuggestion>
  amlAi: AiStageSuggestionState<Pick<AmlScreeningConfig, 'sanctions' | 'pep' | 'adverseMedia'>>
  fraudAi: AiStageSuggestionState<AiIdSuggestion[]>
  tmAi: AiStageSuggestionState<TmRuleConfig>
  workflowAi: AiStageSuggestionState<AiWorkflowSuggestion>
}

export function AccountSetupStage({
  customerMode, onCustomerModeChange, existingCustomer, onExistingCustomerChange, hasDealContext, onRunningChange, onComplete,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneyLoaded, onAiSuggestions, onAiSuggestionPatch,
  aiSuggestions, idvAi, cascadeAi, duplicatesAi, riskAi, kybAi, amlAi, fraudAi, tmAi, workflowAi,
}: AccountSetupStageProps) {
  const [customerName, setCustomerName] = useState('')
  const [accountId, setAccountId] = useState('')
  const [version, setVersion] = useState<'v1' | 'v2'>('v2')
  const [country, setCountry] = useState('AU')
  const [portalUserName, setPortalUserName] = useState('')
  const [portalUserEmail, setPortalUserEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [selectedEnvs, setSelectedEnvs] = useState<EnvId[]>(ENVIRONMENTS.map((e) => e.id))

  const [provisioning, setProvisioning] = useState(false)
  const [currentStep, setCurrentStep] = useState<string | null>(null)
  const [envAccounts, setEnvAccounts] = useState<EnvAccounts[]>([])
  const [savedRefreshKey, setSavedRefreshKey] = useState(0)

  const [objectives, setObjectives] = useState<AiObjectives | null>(null)
  const [objectivesLoading, setObjectivesLoading] = useState(false)
  const [objectivesError, setObjectivesError] = useState<string | null>(null)

  const [aiConfiguring, setAiConfiguring] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiStageKeys, setAiStageKeys] = useState<string[] | null>(null)
  const [chatMessages, setChatMessages] = useState<AiChatMessage[]>([])
  const [chatSending, setChatSending] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)

  // Step 1 of AI Configure: read what this customer is actually optimising
  // for (pass rate, cost, speed, latency, fraud controls, compliance
  // posture) and which jurisdictions they operate in — BEFORE any per-stage
  // vendor/cascade choice is drafted. Shown as an editable card; nothing
  // stage-specific is generated until the rep confirms it (or leaves it as
  // inferred) and clicks through to step 2.
  async function runInferObjectives() {
    if (!existingCustomer?.scopeData) return
    setObjectivesLoading(true)
    setObjectivesError(null)
    setObjectives(null)
    setAiStageKeys(null)
    setAiError(null)
    setChatMessages([])
    setChatError(null)
    const res = await platformJourneyAiApi.inferObjectives(existingCustomer.scopeData)
    setObjectivesLoading(false)
    if (res.success && res.data) {
      setObjectives(res.data)
    } else {
      setObjectivesError(res.error ?? 'Could not read objectives from this Scope record')
    }
  }

  // Step 2: draft per-stage suggestions using the (rep-reviewed) objectives
  // above as a tie-breaker for vendor/cascade choices the Scope facts
  // themselves leave open — an explicit Scope fact always still wins.
  async function runAiConfigureFromScope() {
    if (!existingCustomer?.scopeData) return
    setAiConfiguring(true)
    setAiError(null)
    setAiStageKeys(null)
    // A fresh batch makes any earlier conversation about the previous batch
    // stale — start the chat over rather than let old turns reference
    // suggestions that no longer exist.
    setChatMessages([])
    setChatError(null)
    const res = await platformJourneyAiApi.configureFromScope(existingCustomer.scopeData, objectives)
    setAiConfiguring(false)
    if (res.success && res.data) {
      onAiSuggestions(res.data)
      setAiStageKeys(Object.keys(res.data).filter((k) => k !== 'generatedAt'))
    } else {
      setAiError(res.error ?? 'AI configuration failed')
    }
  }

  const workflowSuggestion = aiSuggestions?.workflow
  const recommendedRecipe = workflowSuggestion?.recipeKey
    ? V1_RECIPES.find((r) => r.key === workflowSuggestion.recipeKey)
    : undefined

  // Only used here to back the checklist below — each entry's `seed`/`apply`
  // stay untouched; toggling a checkbox only ever calls setIncluded, which
  // is the same dismissed/undismissed state every stage's own banner reads.
  const stageAiByKey: Record<string, AiStageSuggestionState<unknown>> = {
    idv: idvAi, cascade: cascadeAi, duplicates: duplicatesAi, risk: riskAi,
    kyb: kybAi, aml: amlAi, fraud: fraudAi, tm: tmAi, workflow: workflowAi,
  }

  // A follow-up conversation about the batch above — answers questions using
  // the same facts/rationale already generated, and can toggle a stage's
  // inclusion or revise its suggested config when the rep clearly asks for a
  // change. Never applies anything itself: a `set_included` action is the
  // same effect as clicking that stage's checkbox, and an `update_suggestion`
  // action just re-opens that one stage's banner with revised content for
  // the rep to apply (or not) themselves.
  async function handleChatSend(text: string) {
    if (!existingCustomer?.scopeData || !aiSuggestions) return
    const nextMessages: AiChatMessage[] = [...chatMessages, { role: 'user', content: text }]
    setChatMessages(nextMessages)
    setChatSending(true)
    setChatError(null)
    const includedStages = (aiStageKeys ?? []).filter((k) => !stageAiByKey[k]?.dismissed)
    const res = await platformJourneyAiApi.chat({
      scopeData: existingCustomer.scopeData,
      objectives,
      suggestions: aiSuggestions,
      includedStages,
      messages: nextMessages,
    })
    setChatSending(false)
    if (res.success && res.data) {
      const { reply, actions } = res.data
      setChatMessages((prev) => [...prev, { role: 'assistant', content: reply }])
      for (const action of actions) {
        if (action.type === 'set_included') {
          stageAiByKey[action.stage]?.setIncluded(action.included)
        } else if (action.type === 'update_suggestion') {
          onAiSuggestionPatch(action.stage, action.suggestion)
        }
      }
    } else {
      setChatError(res.error ?? 'Chat request failed')
    }
  }

  const addWorkflow = useOpsCenterStore((s) => s.addWorkflow)
  const selectWorkflow = useOpsCenterStore((s) => s.selectWorkflow)
  const opsCenterWorkflows = useOpsCenterStore((s) => s.workflows)
  const opsCenterAccounts = useOpsCenterStore((s) => s.accounts)

  const activeEnvironments = ENVIRONMENTS.filter((e) => selectedEnvs.includes(e.id))

  // Creates the journey the first time accounts are provisioned, or updates it
  // if the rep re-provisions after editing fields — either way, the customer
  // itself gets persisted so it can be reloaded and validated later.
  async function persistAccountSetup(built: EnvAccounts[]) {
    const fields = {
      customerName, accountId, version, country, portalUserName, portalUserEmail, notes,
      selectedEnvs, envAccounts: built,
    }
    const id = journeyId ?? (await platformJourneysApi.create(fields)).data?.id
    if (!id) return
    if (journeyId) await platformJourneysApi.update(id, fields)
    const res = await platformJourneysApi.get(id)
    if (res.success && res.data) {
      onJourneyLoaded(res.data)
      setSavedRefreshKey((k) => k + 1)
    }
  }

  function handleLoadJourney(record: PlatformJourneyRecord) {
    setCustomerName(record.customerName)
    setAccountId(record.accountId ?? '')
    setVersion(record.version === 'v1' ? 'v1' : 'v2')
    setCountry(record.country)
    setPortalUserName(record.portalUserName ?? '')
    setPortalUserEmail(record.portalUserEmail ?? '')
    setNotes(record.notes ?? '')
    const envs = Array.isArray(record.selectedEnvs) ? (record.selectedEnvs as EnvId[]) : ENVIRONMENTS.map((e) => e.id)
    setSelectedEnvs(envs)
    const accounts = Array.isArray(record.envAccounts) ? (record.envAccounts as EnvAccounts[]) : []
    setEnvAccounts(accounts)
    onJourneyLoaded(record)
    if (accounts.length > 0) onComplete(accounts, record.customerName)

    const wf = record.workflowConfig as Workflow | null
    if (wf && !opsCenterWorkflows.some((w) => w.id === wf.id)) {
      addWorkflow(opsCenterAccounts[0]?.id ?? 'acc_demo', wf)
    }
    if (wf) selectWorkflow(wf.id)
  }

  async function provision() {
    setProvisioning(true)
    onRunningChange(true)
    const built: EnvAccounts[] = []
    for (const env of activeEnvironments) {
      setCurrentStep(`Creating parent account in ${env.label}…`)
      await sleep(600)
      const parent = makeAccountRecord('parent', customerName, version)
      if (accountId.trim()) parent.accountId = accountId.trim()
      parent.country = country
      parent.portalUserName = portalUserName
      parent.portalUserEmail = portalUserEmail
      parent.notes = notes

      setCurrentStep(`Creating child account in ${env.label}…`)
      await sleep(600)
      const child = makeAccountRecord('child', `${customerName} Child`, version)
      child.country = country

      built.push({ env: env.id, parent, child })
      setEnvAccounts([...built])
    }
    setCurrentStep(null)
    setProvisioning(false)
    onRunningChange(false)
    onComplete(built, customerName)
    await persistAccountSetup(built)
  }

  const canProvision = customerName.trim().length > 0 && selectedEnvs.length > 0 && !provisioning

  const technicalDetailsNode = (
    <>
      <div>
        <label className={labelCls}>Account ID <span className="normal-case font-normal text-[#64748b]">(optional)</span></label>
        <input className={inputCls} value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="auto-generated if left blank" disabled={provisioning} />
      </div>
      <div>
        <label className={labelCls}>Country</label>
        <select className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} disabled={provisioning}>
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
      </div>
      <EnvironmentSelector selected={selectedEnvs} onChange={setSelectedEnvs} disabled={provisioning} />
      <div>
        <SectionLabel>Portal Access</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>Portal user name</label>
            <input className={inputCls} value={portalUserName} onChange={(e) => setPortalUserName(e.target.value)} placeholder="Admin user's name" disabled={provisioning} />
          </div>
          <div>
            <label className={labelCls}>Portal user email</label>
            <input className={inputCls} value={portalUserEmail} onChange={(e) => setPortalUserEmail(e.target.value)} placeholder="admin@example.com" disabled={provisioning} />
          </div>
        </div>
      </div>
      <div>
        <label className={labelCls}>Customer notes</label>
        <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes about this customer…" disabled={provisioning} />
      </div>

      {envAccounts.length > 0 && (
        <div className="space-y-3">
          {envAccounts.map((ea) => (
            <div key={ea.env} className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
              <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <span
                  className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                  style={envBadgeStyle(ea.env)}
                >
                  {ea.env}
                </span>
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <Building2 size={13} className="text-[#4f46e5] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-[#1e293b] truncate">{ea.parent.name}</p>
                    <p className="text-[9px] text-[#64748b]">Parent · {ea.parent.version.toUpperCase()}</p>
                  </div>
                  <span className="text-[9px] font-mono text-[#64748b] shrink-0">{ea.parent.accountId.slice(0, 13)}…</span>
                  <CopyButton text={ea.parent.accountId} />
                </div>
                <div className="flex items-center gap-2.5 pl-6">
                  <ChevronRight size={11} className="text-[#94a3b8] shrink-0" />
                  <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[9px] flex-1 min-w-0" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <Users size={13} className="text-[#64748b] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-[#1e293b] truncate">{ea.child.name}</p>
                      <p className="text-[9px] text-[#64748b]">Child · {ea.child.version.toUpperCase()}</p>
                    </div>
                    <span className="text-[9px] font-mono text-[#64748b] shrink-0">{ea.child.accountId.slice(0, 13)}…</span>
                    <CopyButton text={ea.child.accountId} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <JourneyConfigSummary record={journeyRecord} />

      <VendorRegistrationPanel companyName={customerName} />
      <PlatformConfigPanel />
    </>
  )

  const aiConfigureCard = existingCustomer?.scopeData && (
    <div className="rounded-[14px] p-4 space-y-2.5" style={{ border: '1px solid rgba(99,102,241,.3)', background: 'rgba(99,102,241,.04)' }}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0" style={{ background: 'rgba(99,102,241,.15)' }}>
          <Sparkles size={15} className="text-[#4f46e5]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-[#1e293b]">AI Configure from Scope</p>
          <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">
            Reads {existingCustomer.deal.customer_name}&apos;s DealHub Scope record and proposes pre-built configuration for every in-scope stage below — nothing is applied automatically, you review and accept each stage&apos;s suggestion yourself.
          </p>
        </div>
      </div>
      {!objectives && (
        <button
          onClick={() => void runInferObjectives()}
          disabled={objectivesLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-semibold text-white transition-all disabled:opacity-60"
          style={{ background: '#4f46e5', boxShadow: '0 3px 12px rgba(79,70,229,.35)' }}
        >
          {objectivesLoading
            ? <><Loader2 size={12} className="animate-spin" /> Reading Scope…</>
            : <><Sparkles size={12} /> AI Configure from Scope</>}
        </button>
      )}
      {objectivesError && (
        <p className="text-[10px] text-red-700 flex items-center gap-1.5"><AlertTriangle size={11} className="shrink-0" /> {objectivesError}</p>
      )}
      {objectives && (
        <ObjectivesCard
          objectives={objectives}
          onChange={setObjectives}
          onGenerate={() => void runAiConfigureFromScope()}
          generating={aiConfiguring}
        />
      )}
      {aiError && (
        <p className="text-[10px] text-red-700 flex items-center gap-1.5"><AlertTriangle size={11} className="shrink-0" /> {aiError}</p>
      )}
      {aiStageKeys && !aiError && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-emerald-700 flex items-center gap-1.5">
            <Check size={11} className="shrink-0" /> Generated suggestions for {aiStageKeys.length} stage{aiStageKeys.length === 1 ? '' : 's'} — uncheck any you don&apos;t need, then Accept to apply everything at once (or visit a stage individually to review and apply just that one).
          </p>
          <div className="flex flex-wrap gap-1.5">
            {aiStageKeys.map((k) => {
              const stageAi = stageAiByKey[k]
              const included = !stageAi?.dismissed
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => stageAi?.setIncluded(!included)}
                  className="flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded transition-colors"
                  style={included
                    ? { background: '#ffffff', border: '1px solid #e5e7eb', color: '#475569' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', color: '#94a3b8', textDecoration: 'line-through' }
                  }
                  title={included ? 'Included — click to exclude' : 'Excluded — click to include'}
                >
                  {included ? <CheckSquare size={10} className="shrink-0 text-[#4f46e5]" /> : <Square size={10} className="shrink-0" />}
                  {AI_STAGE_LABELS[k] ?? k}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              for (const k of aiStageKeys) {
                const stageAi = stageAiByKey[k]
                if (stageAi && !stageAi.dismissed) stageAi.apply()
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-semibold text-white transition-all"
            style={{ background: '#15803d', boxShadow: '0 3px 12px rgba(21,128,61,.3)' }}
          >
            <Check size={12} /> Accept all included suggestions
          </button>
        </div>
      )}

      {workflowAi.visible && workflowSuggestion && (
        recommendedRecipe ? (
          <AiSuggestionBanner
            rationale={`Recommended workflow: "${recommendedRecipe.label}" — ${workflowSuggestion.rationale}`}
            confidence={workflowSuggestion.confidence}
            applied={workflowAi.applied}
            onApply={workflowAi.apply}
            onDismiss={workflowAi.dismiss}
          />
        ) : (
          <div className="rounded-[9px] p-2.5 flex items-start gap-2" style={{ background: '#ffffff', border: '1px dashed #e5e7eb' }}>
            <Sparkles size={12} className="text-[#94a3b8] shrink-0 mt-0.5" />
            <p className="text-[10px] text-[#64748b] leading-snug">No workflow recommendation: {workflowSuggestion.rationale}</p>
          </div>
        )
      )}

      {aiStageKeys && !aiError && (
        <AiConfigureChatPanel
          messages={chatMessages}
          sending={chatSending}
          error={chatError}
          onSend={(text) => void handleChatSend(text)}
        />
      )}
    </div>
  )

  return (
    <div className="space-y-4">
    <NewExistingToggle mode={customerMode} onChange={onCustomerModeChange} />

    {customerMode === 'existing' ? (
      <>
      <ExistingCustomerPanel sharedSelection={existingCustomer} onSelectionChange={onExistingCustomerChange} />
      {aiConfigureCard}
      </>
    ) : (
    <>
    <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />

    {/* "AI Configure from Scope" is normally an Existing Customer feature.
        When this journey was opened with a `?dealId=` in the URL, the tool
        shell has already auto-resolved THIS deal's own Scope record here —
        the common case: a rep just finished scoping a new customer's deal
        and now wants AI Configure to draft suggestions from it while walking
        through New Customer provisioning. With no deal context, this is a
        manual "pick any customer to test with" convenience instead. Either
        way, picking/changing a customer here has no effect on the account
        being provisioned above; it only feeds the AI. */}
    <div
      className="rounded-[14px] p-4 space-y-3"
      style={hasDealContext
        ? { border: '1px solid rgba(99,102,241,.3)', background: 'rgba(99,102,241,.03)' }
        : { border: '1px dashed rgba(99,102,241,.35)', background: 'rgba(99,102,241,.02)' }
      }
    >
      <div className="flex items-center gap-2">
        {hasDealContext
          ? <FileSearch size={12} className="text-[#4f46e5] shrink-0" />
          : <FlaskConical size={12} className="text-[#7c3aed] shrink-0" />}
        <p className="text-[11px] font-semibold text-[#1e293b]">
          {hasDealContext ? "This Deal's Scope Record" : 'Test: AI Configure from Scope'}
        </p>
      </div>
      <p className="text-[10px] text-[#64748b] leading-snug -mt-1.5">
        {hasDealContext
          ? "AI Configure suggestions below are sourced from this deal's own DealHub Scope record. Pick a different customer here if you meant to test with someone else — it won't change the account being provisioned above."
          : "Pick any customer's saved DealHub Scope record below to try the same AI-generated configuration suggestions here — useful for testing without needing a real Existing Customer selection."}
      </p>
      <ExistingCustomerPanel
        title="Source Scope Record"
        description="Search for any customer's DealHub Scope record to use as the source for AI-generated suggestions. This doesn't affect the account being provisioned above."
        showCascadeSummary={false}
        sharedSelection={existingCustomer}
        onSelectionChange={onExistingCustomerChange}
      />
      {aiConfigureCard}
    </div>

    {experienceMode === 'guided' && (
      <AccountSetupGuidedView
        customerName={customerName}
        onCustomerNameChange={setCustomerName}
        version={version}
        onVersionChange={setVersion}
        selectedEnvs={selectedEnvs}
        onSelectedEnvsChange={setSelectedEnvs}
        provisioning={provisioning}
        currentStep={currentStep}
        canProvision={canProvision}
        onProvision={() => void provision()}
        envAccounts={envAccounts}
        technicalDetails={technicalDetailsNode}
      />
    )}

    {experienceMode === 'classic' && (
    <>
    <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(320px, 380px) 1fr' }}>
      <div className="rounded-[14px] overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <div className="px-4 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
          <div className="flex items-center gap-2">
            <p className="text-[12px] font-semibold text-[#1e293b]">Account Setup</p>
            <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'rgba(167,139,250,.12)', color: '#7c3aed' }}>
              <FlaskConical size={8} /> Simulated
            </span>
          </div>
          <p className="text-[10px] text-[#64748b] mt-1 leading-snug">
            Mimics the Operations Center&apos;s &quot;Create new customer&quot; flow — the same shell-setup step Implementations runs for every new customer. No live Operations Center API is wired up here yet (it isn&apos;t accessible/documented from this tool), so this mints a parent + child account per environment client-side.
          </p>
        </div>

        <div className="p-4 space-y-3">
          <SavedJourneysPanel onLoad={handleLoadJourney} disabled={provisioning} refreshKey={savedRefreshKey} />

          <div>
            <label className={labelCls}>Customer account name <span style={{ color: '#b91c1c' }}>*</span></label>
            <input className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="e.g. Acme Corp" disabled={provisioning} />
          </div>

          <div>
            <label className={labelCls}>Account ID <span className="normal-case font-normal text-[#64748b]">(optional)</span></label>
            <input className={inputCls} value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="auto-generated if left blank" disabled={provisioning} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Version</label>
              <div className="flex rounded-[7px] overflow-hidden border border-[#e5e7eb]">
                {(['v2', 'v1'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setVersion(v)}
                    disabled={provisioning}
                    className="flex-1 py-1.5 text-[11px] font-semibold uppercase transition-colors"
                    style={version === v ? { background: '#4f46e5', color: '#ffffff' } : { background: '#f8fafc', color: '#64748b' }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Country</label>
              <select className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} disabled={provisioning}>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <EnvironmentSelector selected={selectedEnvs} onChange={setSelectedEnvs} disabled={provisioning} />

          <div>
            <SectionLabel>Portal Access</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Portal user name</label>
                <input className={inputCls} value={portalUserName} onChange={(e) => setPortalUserName(e.target.value)} placeholder="Admin user's name" disabled={provisioning} />
              </div>
              <div>
                <label className={labelCls}>Portal user email</label>
                <input className={inputCls} value={portalUserEmail} onChange={(e) => setPortalUserEmail(e.target.value)} placeholder="admin@example.com" disabled={provisioning} />
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Customer notes</label>
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes about this customer…" disabled={provisioning} />
          </div>

          <button
            onClick={() => void provision()}
            disabled={!canProvision}
            className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
          >
            {provisioning ? (currentStep ?? 'Provisioning…') : 'Provision Parent + Child Accounts'}
          </button>
          {!customerName.trim() && <p className="text-[10px] text-amber-700 text-center">Enter a customer account name first</p>}
          {customerName.trim() && selectedEnvs.length === 0 && <p className="text-[10px] text-amber-700 text-center">Select at least one environment</p>}
          {activeEnvironments.length > 0 && (
            <p className="text-[10px] text-[#475569] text-center">Runs {activeEnvironments.map((e) => e.label).join(' → ')} in sequence</p>
          )}
        </div>
      </div>

      <div className="space-y-3 min-w-0">
        {envAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 rounded-[14px]" style={{ border: '2px dashed #e5e7eb', background: '#f8fafc' }}>
            <Building2 size={22} className="text-[#94a3b8] mb-3" />
            <p className="text-[13px] font-semibold text-[#64748b]">Provision accounts to see the hierarchy</p>
            <p className="text-[11px] text-[#94a3b8] mt-1.5 text-center max-w-xs">A parent + child account shell will be created for each environment.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {envAccounts.map((ea) => (
              <div key={ea.env} className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff', animation: 'fadeUp .25s ease' }}>
                <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                  <span
                    className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                    style={envBadgeStyle(ea.env)}
                  >
                    {ea.env}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  {/* Parent */}
                  <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <Building2 size={13} className="text-[#4f46e5] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-[#1e293b] truncate">{ea.parent.name}</p>
                      <p className="text-[9px] text-[#64748b]">Parent · {ea.parent.version.toUpperCase()}</p>
                    </div>
                    <span className="text-[9px] font-mono text-[#64748b] shrink-0">{ea.parent.accountId.slice(0, 13)}…</span>
                    <CopyButton text={ea.parent.accountId} />
                  </div>
                  {/* Child — nested */}
                  <div className="flex items-center gap-2.5 pl-6">
                    <ChevronRight size={11} className="text-[#94a3b8] shrink-0" />
                    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[9px] flex-1 min-w-0" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                      <Users size={13} className="text-[#64748b] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-[#1e293b] truncate">{ea.child.name}</p>
                        <p className="text-[9px] text-[#64748b]">Child · {ea.child.version.toUpperCase()}</p>
                      </div>
                      <span className="text-[9px] font-mono text-[#64748b] shrink-0">{ea.child.accountId.slice(0, 13)}…</span>
                      <CopyButton text={ea.child.accountId} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {provisioning && currentStep && (
              <p className="text-[11px] text-[#b45309] text-center">{currentStep}</p>
            )}
          </div>
        )}
      </div>
    </div>

    <JourneyConfigSummary record={journeyRecord} />

    <VendorRegistrationPanel companyName={customerName} />
    <PlatformConfigPanel />
    </>
    )}
    </>
    )}
    </div>
  )
}
